import React, { useState } from "react";

export interface DraftsSyncedWithQuery<TDraft> {
    drafts: TDraft[];
    onChange: (drafts: TDraft[]) => void;
}

export const useDraftsSyncedWithQuery = <
    TApi extends { id: unknown },
    TDraft extends { id: TApi["id"] | null },
>(
    entityId: string,
    queryData: TApi[] | undefined,
    toDraft: (item: TApi) => TDraft,
    reconcile: (draft: TDraft, latest: TApi) => TDraft,
): DraftsSyncedWithQuery<TDraft> => {
    const [drafts, setDrafts] = useState<TDraft[]>([]);
    const initializedForEntityId = React.useRef<string | null>(null);
    const hasEdited = React.useRef(false);

    React.useEffect(() => {
        hasEdited.current = false;
        initializedForEntityId.current = null;
    }, [entityId]);

    React.useEffect(() => {
        if (!queryData) {
            return;
        }
        if (!hasEdited.current && initializedForEntityId.current !== entityId) {
            setDrafts(queryData.map(toDraft));
            initializedForEntityId.current = entityId;
            return;
        }
        if (initializedForEntityId.current === entityId) {
            setDrafts(prev =>
                prev.map(draft => {
                    const latest = queryData.find(item => item.id === draft.id);
                    return latest ? reconcile(draft, latest) : draft;
                }),
            );
        }
    }, [queryData, entityId, toDraft, reconcile]);

    const onChange = React.useCallback((next: TDraft[]) => {
        hasEdited.current = true;
        setDrafts(next);
    }, []);

    return { drafts, onChange };
};
